import axios from "axios";
import BASE_URL from "../../config";

// Get the auth token from localStorage and return headers
const getAuthHeader = () => {
  const tokenData = JSON.parse(localStorage.getItem("authUser") || "{}");
  return {
    Authorization: `Bearer ${tokenData.accessToken}`,
    'Content-Type': 'application/json'
  };
};

// API to fetch sales invoices with optional filters
export const getSalesInvoices = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/SalesInvoice/get`, filter, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching sales invoices:", error);
    throw error;
  }
};

// API to insert or update a sales invoice
export const saveSalesInvoice = async (invoiceData) => {
  try {
    console.log("API call - saveSalesInvoice:", invoiceData);

    // If invoiceData is FormData (for file upload), don't set Content-Type
    if (invoiceData instanceof FormData) {
      const headers = getAuthHeader();
      // Remove Content-Type header to let browser set it with boundary for multipart/form-data
      delete headers['Content-Type'];
      const response = await axios.post(`${BASE_URL}/api/SalesInvoice/save`, invoiceData, {
        headers: headers,
      });
      console.log("API response - saveSalesInvoice:", response.data);
      return response.data;
    } else {
      // For regular JSON data
      const response = await axios.post(`${BASE_URL}/api/SalesInvoice/save`, invoiceData, {
        headers: getAuthHeader(),
      });
      console.log("API response - saveSalesInvoice:", response.data);
      return response.data;
    }
  } catch (error) {
    console.error("API error - saveSalesInvoice:", error);
    throw error;
  }
};

// API to get sales invoice by ID
export const getSalesInvoiceById = async (invoiceId) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/SalesInvoice/get`, { Invoice_Id: invoiceId }, {
      headers: getAuthHeader(),
    });
    return response.data?.[0] || null;
  } catch (error) {
    console.error("Error fetching sales invoice by ID:", error);
    throw error;
  }
};

// API to get sales invoice for return
export const getSalesInvoiceForReturn = async (invoiceId) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/SalesInvoice/get-sales-invoice-for-return`, {
      Invoice_Id: invoiceId
    }, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching sales invoice for return:", error);
    throw error;
  }
};

// API to get sales return by ID
export const getSalesReturnById = async (returnInvoiceId) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/SalesInvoice/get-return-by-id`, {
      Return_Invoice_Id: returnInvoiceId
    }, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching sales return by ID:", error);
    throw error;
  }
};

// API to get next invoice number (supports return mode)
export const getNextSalesInvoiceNumber = async (businessId, isReturn = false, returnOriginalInvoiceNo = null) => {
  try {
    const payload = {
      Business_Id: businessId,
      IsReturn: isReturn
    };

    if (isReturn && returnOriginalInvoiceNo) {
      payload.Return_Original_InvoiceNo = returnOriginalInvoiceNo;
    }

    const response = await axios.post(`${BASE_URL}/api/SalesInvoice/get-next-invoice-number`, payload, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error getting next sales invoice number:", error);
    throw error;
  }
};

// API to export invoice report to Excel
export const exportInvoiceReportToExcel = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/SalesInvoice/export-excel`, filter, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `InvoiceReport_${new Date().toISOString().slice(0, 19).replace(/[-:]/g, '').replace('T', '_')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error("Error exporting invoice report:", error);
    throw error;
  }
};

// API to get invoice for print
export const getInvoiceForPrint = async (invoiceId) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/SalesInvoice/get-invoice-for-print`, {
      Invoice_Id: invoiceId
    }, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching invoice for print:", error);
    throw error;
  }
};

// API to upload invoice PDF file to the backend
export const uploadInvoicePdf = async (file) => {
  try {
    const formData = new FormData();
    formData.append("file", file, "invoice.pdf");
    const headers = getAuthHeader();
    // Remove Content-Type to let browser set it automatically with boundary
    delete headers['Content-Type'];
    const response = await axios.post(`${BASE_URL}/api/SalesInvoice/upload-pdf`, formData, {
      headers: headers,
    });
    return response.data;
  } catch (error) {
    console.error("Error uploading invoice PDF:", error);
    throw error;
  }
};

// API to send invoice via WhatsApp (summary or PDF)
export const sendInvoiceWhatsApp = async (payload) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/WhatsAppMessageLog/send-invoice-whatsapp`, payload, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error sending invoice WhatsApp:", error);
    throw error;
  }
};

// API to get customer advance balance
export const getCustomerAdvanceBalance = async (customerId) => {
  try {
    // Send CustomerId with BusinessId and BranchId as null
    const request = {
      CustomerId: parseInt(customerId, 10),
      BusinessId: null,
      BranchId: null
    };

    console.log('🔍 API Call - getCustomerAdvanceBalance:', request);

    const response = await axios.post(
      `${BASE_URL}/api/Receipt/get-customer-advance-balance`,
      request,
      { headers: getAuthHeader() }
    );

    console.log('✅ API Response - getCustomerAdvanceBalance:', response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Error getting customer advance balance:", error);
    console.error("❌ Error details:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
};

// API to get customer outstanding credit
export const getCustomerOutstandingCredit = async (customerId) => {
  try {
    // Send CustomerId with BusinessId and BranchId as null
    const request = {
      CustomerId: parseInt(customerId, 10),
      BusinessId: null,
      BranchId: null
    };

    console.log('🔍 API Call - getCustomerOutstandingCredit:', request);

    const response = await axios.post(
      `${BASE_URL}/api/Receipt/get-customer-outstanding-credit`,
      request,
      { headers: getAuthHeader() }
    );

    console.log('✅ API Response - getCustomerOutstandingCredit:', response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Error getting customer outstanding credit:", error);
    console.error("❌ Error details:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
};

// API to fetch invoice report summary (grouped by date)
export const getInvoiceReportSummary = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/SalesInvoice/get-report-summary`, filter, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching invoice report summary:", error);
    throw error;
  }
};

// API to fetch invoice report details for a specific date
export const getInvoiceReportDetails = async (request = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/SalesInvoice/get-report-details`, request, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching invoice report details:", error);
    throw error;
  }
};

// API to export invoice report details for a specific date to Excel
export const exportInvoiceReportDetailsToExcel = async (request = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/SalesInvoice/export-report-details-excel`, request, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const dateStr = request.date ? new Date(request.date).toISOString().slice(0, 10).replace(/-/g, '') : '';
    link.setAttribute('download', `InvoiceReport_${dateStr}_${new Date().toISOString().slice(0, 19).replace(/[-:]/g, '').replace('T', '_')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error("Error exporting invoice report details:", error);
    throw error;
  }
};

// API to export invoice report summary (consolidated) to Excel
export const exportInvoiceReportSummaryToExcel = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/SalesInvoice/export-report-summary-excel`, filter, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const fromDateStr = filter.fromDate ? new Date(filter.fromDate).toISOString().slice(0, 10).replace(/-/g, '') : 'All';
    const toDateStr = filter.toDate ? new Date(filter.toDate).toISOString().slice(0, 10).replace(/-/g, '') : 'All';
    link.setAttribute('download', `InvoiceReport_Summary_${fromDateStr}_to_${toDateStr}_${new Date().toISOString().slice(0, 19).replace(/[-:]/g, '').replace('T', '_')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error("Error exporting invoice report summary:", error);
    throw error;
  }
};

// API to get customer loyalty balance for a specific branch
export const getCustomerBranchLoyaltyBalance = async (customerId, branchId, businessId) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/LoyaltyPoints/get-customer-branch-balance`, {
      CustomerId: customerId,
      BranchId: branchId,
      BusinessId: businessId
    }, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching customer branch loyalty balance:", error);
    throw error;
  }
};

// API to get active loyalty config
export const getActiveLoyaltyConfig = async (businessId, branchId, date) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/LoyaltyPoints/get-active-config`, {
      BusinessId: businessId,
      BranchId: branchId,
      Date: date
    }, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching active loyalty config:", error);
    throw error;
  }
};

export const getTopSoldItems = async (businessId, branchId = null) => {
    try {
        const payload = {
            businessId: parseInt(businessId),
            branchId: branchId ? parseInt(branchId) : null
        };
        const response = await axios.post(`${BASE_URL}/api/SalesInvoice/get-top-sold-items`, payload, {
            headers: getAuthHeader(),
        });
        return response.data;
    } catch (error) {
        console.error("Error fetching top sold items:", error);
        throw error;
    }
};

// Service Monthly Report
export const getServiceMonthlyReport = async (filter = {}) => {
    try {
        // Ensure dates are properly formatted
        const formattedFilter = {
            ...filter,
            FromDate: filter.fromDate instanceof Date ? filter.fromDate.toISOString() : filter.fromDate,
            ToDate: filter.toDate instanceof Date ? filter.toDate.toISOString() : filter.toDate,
            Business_Id: filter.businessId,
            Branch_Id: filter.branchId
        };

        const response = await axios.post(`${BASE_URL}/api/SalesInvoice/get-service-monthly-report`, formattedFilter, {
            headers: getAuthHeader(),
        });
        return response.data;
    } catch (error) {
        console.error("Error fetching service monthly report:", error);
        throw error;
    }
};

export const exportServiceMonthlyReportToExcel = async (filter = {}) => {
    try {
        const formattedFilter = {
            ...filter,
            FromDate: filter.fromDate instanceof Date ? filter.fromDate.toISOString() : filter.fromDate,
            ToDate: filter.toDate instanceof Date ? filter.toDate.toISOString() : filter.toDate,
            Business_Id: filter.businessId,
            Branch_Id: filter.branchId
        };

        const response = await axios.post(`${BASE_URL}/api/SalesInvoice/export-service-monthly-excel`, formattedFilter, {
            headers: getAuthHeader(),
            responseType: 'blob'
        });
        
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const fromDateStr = filter.fromDate ? new Date(filter.fromDate).toISOString().slice(0, 10).replace(/-/g, '') : 'All';
        const toDateStr = filter.toDate ? new Date(filter.toDate).toISOString().slice(0, 10).replace(/-/g, '') : 'All';
        link.setAttribute('download', `ServiceMonthlyReport_${fromDateStr}_to_${toDateStr}_${new Date().toISOString().slice(0,19).replace(/[-:]/g, '').replace('T', '_')}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        return true;
    } catch (error) {
        console.error("Error exporting service monthly report:", error);
        throw error;
    }
};

// API to fetch tax invoice report (flat list)
export const getTaxInvoiceReport = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/SalesInvoice/get-tax-invoice-report`, filter, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching tax invoice report:", error);
    throw error;
  }
};

// API to export tax invoice report to Excel
export const exportTaxInvoiceReportToExcel = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/SalesInvoice/export-tax-invoice-report-excel`, filter, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const fromDateStr = filter.fromDate ? new Date(filter.fromDate).toISOString().slice(0, 10).replace(/-/g, '') : 'All';
    const toDateStr = filter.toDate ? new Date(filter.toDate).toISOString().slice(0, 10).replace(/-/g, '') : 'All';
    link.setAttribute('download', `TaxInvoiceReport_${fromDateStr}_to_${toDateStr}_${new Date().toISOString().slice(0, 19).replace(/[-:]/g, '').replace('T', '_')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error("Error exporting tax invoice report:", error);
    throw error;
  }
};

// API to fetch campaigns valid for a given invoice date, business and optionally source
// Matches campaigns where invoiceDate is between ValidFrom and ValidTo
// If sourceId is provided, also filters by sourceId
export const getCampaignsForInvoice = async (businessId, invoiceDate, sourceId = null) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Campaign/get`, {
      BusinessId: businessId,
      Page: 1,
      PageSize: 1000
    }, {
      headers: getAuthHeader(),
    });

    let all = response.data?.campaigns || response.data?.Campaigns || [];

    // Filter by date range
    if (invoiceDate) {
      const date = new Date(invoiceDate);
      date.setHours(0, 0, 0, 0);
      all = all.filter(c => {
        const from = new Date(c.validFrom || c.ValidFrom);
        const to = new Date(c.validTo || c.ValidTo);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return date >= from && date <= to;
      });
    }

    // Filter by source if provided
    if (sourceId) {
      all = all.filter(c => (c.sourceId || c.SourceId) === sourceId);
    }

    return all;
  } catch (error) {
    console.error("Error fetching campaigns for invoice:", error);
    return [];
  }
};
